export interface CommentFormConfig {
    formWrapper: HTMLElement;
    commentsWrapper: HTMLElement;
    form: HTMLFormElement;
    guestAuth: HTMLElement;
    providers: HTMLElement;
    textarea: HTMLTextAreaElement;
    post: HTMLElement;
    spinner: HTMLElement;
}

type ServerResponse =  {
    status: string;
    data: {
        redirect: string,
        template: string,
        [key: string]: string
    },
    errors: any[]
}

export class CommentForm {
    protected isLocked: boolean = false;
    protected activeProvider: HTMLElement = null;
    protected requireAuth: string;
    protected authorized: string;
    protected authSource: string;
    protected readonly elements: {
        formWrapper: HTMLElement;
        commentsWrapper: HTMLElement;
        form: HTMLFormElement;
        guestAuth: HTMLElement;
        providers: HTMLElement;
        textarea: HTMLTextAreaElement;
        post: HTMLElement;
        spinner: HTMLElement;
    };

    constructor(options: CommentFormConfig) {
        this.elements = options;

        const {commentsWrapper} = this.elements;
        this.requireAuth =  commentsWrapper.getAttribute(`data-auth`);
        this.authorized = commentsWrapper.getAttribute(`data-authorized`);
        this.authSource = commentsWrapper.getAttribute(`data-source`);

        this.bind();
    }

    protected onReply(link: Element): void {
        const
            comment = link.closest(`.comment`),
            comments = document.querySelectorAll(`.comment`),
            commentID = comment.getAttribute(`data-comment-id`),
            actionWrapper = link.closest(`div`),
            replyClass = "in-reply-to";

        this.moveForm(actionWrapper, commentID, true);
        comments.forEach(it => {
            it.classList.remove(replyClass);
        });
        comment.classList.add(replyClass);
    }

    protected onSubmit(evt: Event): void {
        if (!this.isLocked) {
            evt.preventDefault();

            const
                {form, spinner} = this.elements,
                url = `${form.getAttribute('action')}?json=1`;

            this.isLocked = true;
            spinner.style.display = `block`;

            fetch(url, {
                method: 'POST',
                body: new FormData(form)
            })
                .then(response => response.json())
                .catch(error => this.refreshForm(false))
                .then(json => this.responseHandler(json));
        }
    }

    protected responseHandler(response: ServerResponse): void {
        const {form, commentsWrapper, post, spinner} = this.elements;

        if ( response.status && response.status === `ok` && response.data) {
            if (response.data.redirect) {
                window.location.replace(response.data.redirect);
                window.location.href = response.data.redirect;
            } else {
                const comment = response.data.template,
                    count = response.data[`count_str`],
                    hiddenClass = `is-hidden`,
                    newClass = `is-new`;

                let target = form.closest(`.comment__wrapper`);

                // If first comment
                if (!target) {
                    commentsWrapper.classList.remove(hiddenClass);
                    target = commentsWrapper.querySelector(`.comments`);
                }

                // Render new comment
                target.insertAdjacentHTML(<"beforebegin" | "afterbegin" | "beforeend" | "afterend">"beforeEnd", `<div class='comment__wrapper' /> ${comment}`);
                const newComment = target.lastChild as Element;

                // marking
                newComment.classList.add(newClass);
                setTimeout(() => newComment.classList.remove(newClass), 10000);

                // scroll
                newComment.scrollIntoView({block: "center", behavior: "smooth"});

                // Reset comment form
                this.refreshForm(true);
                this.moveForm(post, ``, false);

                // Change counter
                const counter = commentsWrapper.querySelector(`.comments-count`);
                if (counter) counter.textContent = count;
            }
        } else if ( response.status && response.status === `fail` ) {
            const errors = response.errors;

            this.refreshForm(false);

            errors.forEach(error => {
                for (let name in error) {
                    if (error.hasOwnProperty(name)) {
                        const element = form.querySelector(`[name=${name}]`);
                        if (element) {
                            let message = document.createElement(`em`);
                            message.innerHTML = error[name];
                            message.classList.add(`errormsg`);
                            element.classList.add(`error`);
                            element.after(message);
                        }
                    }
                }
            });
        } else {
            this.refreshForm(false);
        }

        this.isLocked = false;
        spinner.style.display = `none`;
    }

    protected onProviderClick(evt: Event, link: Element, isGuest: boolean = false): void {
        evt.preventDefault();

        const
            {form, guestAuth} = this.elements,
            li = link.closest(`li`),
            captcha = form.querySelector(`.wa-captcha`) as HTMLElement,
            user = form.querySelector(`.auth-user`) as HTMLElement,
            provider = li.getAttribute(`data-provider`);

        let isSelected = li.classList.contains(`is-selected`);

        // Show window
        if (!isSelected && !isGuest && provider) {
            const width = 600,
                height = 400,
                left = (screen.width - width) / 2,
                top = (screen.height - height) / 2,
                url = link.getAttribute(`href`);

            let href = this.requireAuth ? `${url}&guest=1` : url;

            window.open(href,`oauth`, `width=${width},height=${height},left=${left},top=${top},status=no,toolbar=no,menubar=no`);
        }

        // Show captcha
        if (isGuest) {
            guestAuth.style.display = `grid`;
            if (captcha) captcha.style.display = `block`;
            if (user) user.style.display = `none`;
        } else {
            guestAuth.style.display = `none`;
            if (captcha) captcha.style.display = `none`;
            if (user) user.style.display = `flex`;
        }

        // Render link
        if (this.activeProvider) {
            this.activeProvider.classList.remove(`is-selected`);
        } else {
            const providers = this.elements.providers.querySelectorAll(`li`);
            providers.forEach( provider => {
                provider.classList.remove(`is-selected`);
            });
        }

        li.classList.add(`is-selected`);
        this.activeProvider = li;

        // Set data
        const authProvider = form.querySelector(`input[name=auth_provider]`) as HTMLInputElement;
        authProvider.value = provider;
    }

    public moveForm(target: Element, id: string, setFocus: boolean): void {
        const
            {form, formWrapper, textarea} = this.elements,
            parent = form.querySelector(`input[name=parent]`) as HTMLInputElement;

        this.refreshForm(true);
        parent.value = (id) ? id : ``;
        target.insertAdjacentElement('afterend', formWrapper);
        if (setFocus) textarea.focus();
    }

    public refreshForm(reset: boolean): void {
        const
            {form, textarea} = this.elements,
            messages = form.querySelectorAll(`.errormsg`),
            errors = form.querySelectorAll(`.error`),
            captcha = form.querySelector(`.wa-captcha-refresh`) as HTMLButtonElement;

        messages.forEach(message => message.remove());
        errors.forEach(error => error.classList.remove(`error`));
        if (captcha) captcha.click();

        if (reset) {
            form.reset();
            textarea.value = ``;
        }
    }

    protected bind(): void {
        const
            {post, form} = this.elements,
            commentReply = document.querySelectorAll(`.comment-reply`),
            cancelButtons = document.querySelectorAll(`.comment-cancel`),
            providerLinks = document.querySelectorAll(`.provider-link a`),
            providerGuest = document.querySelector(`.guest-provider a`);

        if (commentReply.length) {
            commentReply.forEach( it => {
                it.addEventListener( 'click', () => this.onReply(it));
            });
        }

        cancelButtons.forEach( it => {
            it.addEventListener( 'click', () => this.moveForm(post, ``, false));
        });

        form.addEventListener('submit', (evt) => this.onSubmit(evt));

        if (providerLinks.length) {
            providerLinks.forEach( it => {
                it.addEventListener( 'click', (evt) => this.onProviderClick(evt, it));
            });
        }

        if (providerGuest) {
            providerGuest.addEventListener( 'click', (evt) => this.onProviderClick(evt, providerGuest, true));
        }
    }
}